import React, { useState, useEffect } from 'react';
import { usersAPI, jobsAPI, companiesAPI, rolesAPI } from '../../services/api';
import { formatDate } from '../../utils/helpers';
import { FiUsers, FiBriefcase, FiTrash2, FiRefreshCw, FiSearch, FiPlus, FiX } from 'react-icons/fi';
import toast from 'react-hot-toast';
import './Admin.css';

const Admin = () => {
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Company creation state
  const [showCreateCompany, setShowCreateCompany] = useState(false);
  const [companyForm, setCompanyForm] = useState({
    name: '', description: '', industry: '', website: '', location: '', size: '', email: '', phone: '',
  });
  const [savingCompany, setSavingCompany] = useState(false);

  useEffect(() => { loadData(); }, [tab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (tab === 'users') {
        const { data } = await usersAPI.list({ include_deleted: true });
        setUsers(data.data || data.users || (Array.isArray(data) ? data : []));
      } else if (tab === 'jobs') {
        const { data } = await jobsAPI.list({ include_deleted: true });
        setJobs(data.data || data.jobs || (Array.isArray(data) ? data : []));
      } else if (tab === 'companies') {
        const { data } = await companiesAPI.list({ include_deleted: true });
        setCompanies(data.data || data.companies || (Array.isArray(data) ? data : []));
      }
    } catch (e) {}
    setLoading(false);
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Soft delete this record?')) return;
    try {
      if (type === 'user') await usersAPI.delete(id);
      else if (type === 'job') await jobsAPI.delete(id);
      else if (type === 'company') await companiesAPI.delete(id);
      toast.success('Record deleted');
      loadData();
    } catch (e) { toast.error('Delete failed'); }
  };

  const handleRestore = async (type, id) => {
    try {
      if (type === 'user') await usersAPI.restore(id);
      else if (type === 'job') await jobsAPI.restore(id);
      else if (type === 'company') await companiesAPI.restore(id);
      toast.success('Record restored');
      loadData();
    } catch (e) { toast.error('Restore failed'); }
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    if (!companyForm.name.trim()) { toast.error('Company name is required'); return; }
    setSavingCompany(true);
    try {
      // Only send non-empty fields to avoid backend validation errors
      const payload = {};
      Object.entries(companyForm).forEach(([key, val]) => {
        if (val && String(val).trim()) payload[key] = String(val).trim();
      });
      await companiesAPI.create(payload);
      toast.success('Company created successfully!');
      setShowCreateCompany(false);
      setCompanyForm({ name: '', description: '', industry: '', website: '', location: '', size: '', email: '', phone: '' });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.error || 'Failed to create company');
    }
    setSavingCompany(false);
  };

  return (
    <div className="admin-page">
      <div className="container">
        <div className="page-header"><h1>Admin Panel</h1><p>Manage users, jobs, and companies. Only admins can restore deleted records.</p></div>

        <div className="tabs">
          <button className={`tab ${tab === 'users' ? 'active' : ''}`} onClick={() => setTab('users')}><FiUsers /> Users</button>
          <button className={`tab ${tab === 'jobs' ? 'active' : ''}`} onClick={() => setTab('jobs')}><FiBriefcase /> Jobs</button>
          <button className={`tab ${tab === 'companies' ? 'active' : ''}`} onClick={() => setTab('companies')}>Companies</button>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', alignItems: 'center' }}>
          <div className="search-bar" style={{ flex: 1, marginBottom: 0 }}>
            <FiSearch className="icon" />
            <input type="text" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          {tab === 'companies' && (
            <button className="btn btn-primary btn-sm" onClick={() => setShowCreateCompany(!showCreateCompany)}>
              {showCreateCompany ? <><FiX /> Cancel</> : <><FiPlus /> Add Company</>}
            </button>
          )}
        </div>

        {/* Company creation form (admin) */}
        {tab === 'companies' && showCreateCompany && (
          <div className="card" style={{ padding: '1.25rem', marginBottom: '1rem', borderLeft: '3px solid var(--accent-primary, #3b82f6)' }}>
            <form onSubmit={handleCreateCompany}>
              <strong style={{ display: 'block', marginBottom: '0.75rem' }}>Create New Company</strong>
              <div className="grid-2" style={{ marginBottom: '0.75rem' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Company Name *</label>
                  <input type="text" placeholder="Acme Pty Ltd" value={companyForm.name} onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })} />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Industry</label>
                  <select value={companyForm.industry} onChange={(e) => setCompanyForm({ ...companyForm, industry: e.target.value })}>
                    <option value="">Select</option>
                    {['Technology','Finance','Healthcare','Education','Manufacturing','Retail','Construction','Mining','Agriculture','Telecommunications','Legal','Marketing','Consulting','Other'].map(i => <option key={i} value={i.toLowerCase()}>{i}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group" style={{ margin: '0 0 0.75rem' }}>
                <label>Description</label>
                <textarea rows="2" placeholder="Brief description..." value={companyForm.description} onChange={(e) => setCompanyForm({ ...companyForm, description: e.target.value })} />
              </div>
              <div className="grid-2" style={{ marginBottom: '0.75rem' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Website</label>
                  <input type="url" placeholder="https://company.co.za" value={companyForm.website} onChange={(e) => setCompanyForm({ ...companyForm, website: e.target.value })} />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Location</label>
                  <input type="text" placeholder="Johannesburg, Gauteng" value={companyForm.location} onChange={(e) => setCompanyForm({ ...companyForm, location: e.target.value })} />
                </div>
              </div>
              <div className="grid-2" style={{ marginBottom: '0.75rem' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Company Size</label>
                  <select value={companyForm.size} onChange={(e) => setCompanyForm({ ...companyForm, size: e.target.value })}>
                    <option value="">Select size</option>
                    <option value="1-10">1-10 employees</option>
                    <option value="11-50">11-50 employees</option>
                    <option value="51-200">51-200 employees</option>
                    <option value="201-500">201-500 employees</option>
                    <option value="501+">501+ employees</option>
                  </select>
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Company Email</label>
                  <input type="email" placeholder="hr@company.co.za" value={companyForm.email} onChange={(e) => setCompanyForm({ ...companyForm, email: e.target.value })} />
                </div>
              </div>
              <div className="form-group" style={{ margin: '0 0 0.75rem' }}>
                <label>Company Phone</label>
                <input type="tel" placeholder="+27 XX XXX XXXX" value={companyForm.phone} onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })} style={{ maxWidth: '250px' }} />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button type="submit" className="btn btn-primary btn-sm" disabled={savingCompany}>{savingCompany ? 'Creating...' : 'Create Company'}</button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowCreateCompany(false)}>Cancel</button>
              </div>
            </form>
          </div>
        )}

        {loading ? <div className="loading-container"><div className="spinner" /></div> : (
          <div className="table-wrapper">
            {tab === 'users' && (
              <table>
                <thead><tr><th>Name</th><th>Email</th><th>Roles</th><th>Joined</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>{users.filter(u => `${u.first_name} ${u.last_name} ${u.email}`.toLowerCase().includes(search.toLowerCase())).map(u => (
                  <tr key={u.id} style={u.deleted_at ? { opacity: 0.5 } : {}}>
                    <td>{u.first_name} {u.last_name}</td>
                    <td>{u.email}</td>
                    <td>{(u.roles || []).map(r => typeof r === 'string' ? r : r.name).join(', ') || '-'}</td>
                    <td>{formatDate(u.created_at)}</td>
                    <td><span className={`badge ${u.deleted_at ? 'badge-danger' : 'badge-success'}`}>{u.deleted_at ? 'Deleted' : 'Active'}</span></td>
                    <td>
                      {u.deleted_at ? (
                        <button className="btn btn-sm btn-success" onClick={() => handleRestore('user', u.id)}><FiRefreshCw /> Restore</button>
                      ) : (
                        <button className="btn btn-sm btn-danger" onClick={() => handleDelete('user', u.id)}><FiTrash2 /></button>
                      )}
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            )}
            {tab === 'jobs' && (
              <table>
                <thead><tr><th>Title</th><th>Company</th><th>Type</th><th>Posted</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>{jobs.filter(j => `${j.title} ${j.company_name}`.toLowerCase().includes(search.toLowerCase())).map(j => (
                  <tr key={j.id} style={j.deleted_at ? { opacity: 0.5 } : {}}>
                    <td>{j.title || j.name}</td>
                    <td>{j.company_name || '-'}</td>
                    <td><span className="badge">{j.job_type || 'Full-time'}</span></td>
                    <td>{formatDate(j.created_at)}</td>
                    <td><span className={`badge ${j.deleted_at ? 'badge-danger' : 'badge-success'}`}>{j.deleted_at ? 'Deleted' : 'Active'}</span></td>
                    <td>
                      {j.deleted_at ? (
                        <button className="btn btn-sm btn-success" onClick={() => handleRestore('job', j.id)}><FiRefreshCw /> Restore</button>
                      ) : (
                        <button className="btn btn-sm btn-danger" onClick={() => handleDelete('job', j.id)}><FiTrash2 /></button>
                      )}
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            )}
            {tab === 'companies' && (
              <table>
                <thead><tr><th>Name</th><th>Industry</th><th>Location</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>{companies.filter(c => `${c.name} ${c.industry}`.toLowerCase().includes(search.toLowerCase())).map(c => (
                  <tr key={c.id} style={c.deleted_at ? { opacity: 0.5 } : {}}>
                    <td>{c.name}</td>
                    <td>{c.industry || '-'}</td>
                    <td>{c.location || '-'}</td>
                    <td><span className={`badge ${c.deleted_at ? 'badge-danger' : 'badge-success'}`}>{c.deleted_at ? 'Deleted' : 'Active'}</span></td>
                    <td>
                      {c.deleted_at ? (
                        <button className="btn btn-sm btn-success" onClick={() => handleRestore('company', c.id)}><FiRefreshCw /> Restore</button>
                      ) : (
                        <button className="btn btn-sm btn-danger" onClick={() => handleDelete('company', c.id)}><FiTrash2 /></button>
                      )}
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
